import {UniqueIdentifier} from "@dnd-kit/core";
import DocumentStore from "../store/document-store";
import {Title} from "../../entity/model/title";
import {MoveDocumentDto} from "../../entity/dto/move-document-dto";
import {RequestBody} from "../../entity/core/request-body";
import TitleService from "./title-service";
import {toast} from "react-toastify";
import {DocumentRunnerDto} from "../../entity/dto/document-runner-dto";
import {ValidateResponse} from "../../entity/resource/validate-response";
import axios from "axios";
import {PDFDocument} from 'pdf-lib';
import {DropdownOption} from "../../entity/core/dropdown-option";
import {SplitDocumentDto} from "../../entity/dto/split-document-dto";
import {SplitAIDto} from "../../entity/dto/split-ai-dto";
import {DocumentDto} from "../../entity/dto/title-template-dto";
import {Document} from "../../entity/resource/document";
import {Section} from "../../entity/resource/section";
import {PublishDocumentDto} from "../../entity/dto/publish-document-dto";
import {TitleTemplate} from "../../entity/model/title-template";
import {SplitDocumentRequestDto} from "../../entity/dto/split-document-request-dto";
import {DocumentRunnerSearchDto} from "../../entity/dto/document-runner-search-dto";
import {DocumentRunnerConfig} from "../../entity/resource/scraper-config-resource";
import {SplitDocumentRequest} from "../../entity/resource/split-document-request";


export default class DocumentService {
    documentStore: DocumentStore;
    titleService: TitleService;

    constructor(documentStore: DocumentStore, titleService: TitleService) {
        this.documentStore = documentStore;
        this.titleService = titleService;
    }

    setDocumentMoving = (status: boolean): void => {
        this.documentStore.isDocumentMoving = status;
    }

    setMovingDocumentId = (documentId?: UniqueIdentifier): void => {
        if (documentId)
            this.documentStore.movingDocumentId = documentId;
    }

    moveDocument = async (organizationId: string, documentId: UniqueIdentifier | string, activeSection: string | UniqueIdentifier, targetSection: string, sectionIndex: number, titleId?: string, templateId?: string): Promise<Title | TitleTemplate | undefined> => {
        const moveDocumentDto: MoveDocumentDto = {
            documentID: documentId,
            targetSectionID: targetSection,
            targetSectionIndex: sectionIndex
        };
        const requestBody: RequestBody = {
            body: moveDocumentDto
        }

        const response = await this.documentStore.moveDocument(organizationId, activeSection, requestBody, titleId, templateId);
        if (typeof response !== 'string')
            return response;
        else
            toast.error(response);
    }

    deleteDocument = async (organizationId: string, sectionId: string, documentId: UniqueIdentifier, lastDocument: boolean, titleId?: string, templateId?: string): Promise<void> => {
        const response = await this.documentStore.deleteDocument(organizationId, sectionId, documentId, titleId, templateId);

        if (!response)
            if (lastDocument)
                toast.success('Document deleted successfully !!');
        else
            toast.error(response);
    }

    updateDocument = async (organizationId: string, sectionId: string, documentId: string, name: string, titleId?: string, templateId?: string): Promise<void> => {
        const documentRequest = {name: name};
        const requestBody: RequestBody = {body: documentRequest};
        const response = await this.documentStore.updateDocument(organizationId, sectionId, documentId, requestBody, titleId, templateId);

        if (!response)
            toast.success('Document updated successfully !!');
        else
            toast.error(response);
    }

    setDocument = (document?: Document): void => {
        this.documentStore.document = document;
    }

    setDocumentSection = (section?: Section): void => {
        this.documentStore.section = section;
    }

    getMovingDocumentId = (): UniqueIdentifier => {
        return this.documentStore.movingDocumentId;
    }

    getDocumentMoving = (): boolean => {
        return this.documentStore.isDocumentMoving;
    }

    uploadFile = async (organizationId: string, file: any, titleId?: string, sectionId?: string, setFileProgress?: (progress: number) => void, titleTemplateId?: string): Promise<DocumentDto | undefined> => {
        const preSignedRequest = {
            uploadType: 'DOCUMENT',
            mimeType: 'application/pdf',
        };
        const requestBody: RequestBody = {body: preSignedRequest}

        const response = await this.documentStore.getPreSignedUrl(organizationId, requestBody, titleId, titleTemplateId);

        if (typeof response === 'string')
            toast.error(response)
        else {
            const result = await this.documentStore.s3FileUpload(file, response.signedURL, setFileProgress);
            const fileName = file.name.split('.pdf');

            if (result) {
                const document: DocumentDto = {
                    id: fileName.length > 0 ? fileName[0] : file.name,
                    name: fileName.length > 0 ? fileName[0] : file.name,
                    url: response.objectURL
                };

                const requestBody: RequestBody = {
                    body: document
                }

                if (titleId)
                    return await this.documentStore.uploadDocument(organizationId, titleId, requestBody, sectionId);
                else
                    return document;
            }
        }
    }

    uploadLogo = async (organizationId: string, file: File, titleId?: string): Promise<string | undefined> => {
        const preSignedRequest = {
            uploadType: 'DOCUMENT',
            mimeType: 'application/pdf',
        };
        const requestBody: RequestBody = { body: preSignedRequest };

        const response = await this.documentStore.getPreSignedUrl(organizationId, requestBody, titleId);

        if (typeof response === 'string')
            toast.error(response);
        else {
            const result = await this.documentStore.s3FileUpload(file, response.signedURL);

            if (result) {
                toast.success('Logo uploaded successfully');
                return response.objectURL;
            } else {
                toast.error('Failed to upload logo');
            }
        }
    }

    getDocument = (): Document | undefined => {
        return this.documentStore.document;
    }

    geDocumentSection = (): Section | undefined => {
        return this.documentStore.section;
    }

    createMergePdf = async (organizationId: string, titleId: string, documentIds: string[], batchID: string): Promise<void> => {
        return await this.documentStore.createMergePdf(organizationId, titleId, documentIds, batchID);
    }

    validateDocument = async (organizationId: string, titleId: string, documentIDs: string[], batchId: string): Promise<ValidateResponse | undefined> => {
        const response = await this.documentStore.validateDocuments(organizationId, titleId, documentIDs, batchId);

        if (response && typeof response !== 'string')
            return response;
    }

    documentRunner = async (organizationId: string, tmsNumber: string, batchId: string, config?: DocumentRunnerConfig, title?: Title): Promise<void> => {
        const documentRunnerDto: DocumentRunnerDto = {
            priority: config?.priority ?? '',
            titleID: title?.id ?? '',
            countyCode: config?.code ?? '',
            tmsNumbers: tmsNumber === '' ? title?.tmsParcelNumbers ?? [] : tmsNumber.split(',').map(item => item.trim()),
            proxy: config?.proxy ?? '',
            retryProxy: config?.retryProxy ?? '',
            batchID: batchId,
            sectionToScrape: config?.scraperSection,
            scrapeToProjectTitle: true
        };

        const response = await this.documentStore.documentRunner(organizationId, documentRunnerDto);
        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    getPageCount = async (url: string): Promise<number> => {
        try {
            const response = await axios.get(url, { responseType: 'arraybuffer' });
            const pdfBuffer = response.data;
            const pdfDoc = await PDFDocument.load(pdfBuffer);

            return pdfDoc.getPages().length;
        } catch (error) {
            console.error('Error reading PDF from URL:', error);
            return 0;
        }
    }

    getSplitRequest = (): SplitDocumentRequestDto => {
        return {
            name: '',
            documentType:  '',
            startPage: 0,
            endPage: 0
        }
    }

    getSplitPages = (start: number, end: number): number[] => {
        if (start === end)
            return [start];

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    splitDocument = async (organizationId: string, titleId: string, splitDocumentDto: SplitDocumentDto): Promise<boolean> => {
        return typeof await this.documentStore.splitDocument(organizationId, titleId, {body: splitDocumentDto}) !== 'string';
    }

    getModelOptions = (): DropdownOption[] => {
        const options: DropdownOption[] = [];
        options.push({key: 'google', value: 'google', text: 'Google'});
        options.push({key: 'bert', value: 'bert', text: 'Bert'});

        return options;
    }

    splitWithAI = async (splitAIDto: SplitAIDto): Promise<void> => {
        const response = await this.documentStore.splitWithAI(splitAIDto);
        if (typeof response === 'string')
            toast.error(response);
    }

    publishDocument = async (organizationId: string, titleTemplateId: string, titleId: string, publishDocumentDto: PublishDocumentDto): Promise<void> => {
        const response = await this.documentStore.publishDocument(organizationId, titleTemplateId, titleId, { body: publishDocumentDto });

        if (typeof response !== 'string')
            toast.success('Document published successfully !!');
        else
            toast.error(response);
    }

    getDocumentRunnerSearchDto = (tmsNumbers: string[]): DocumentRunnerSearchDto => {
        return {
            tmsNumber: tmsNumbers.join(', ')
        };
    }

    getSplitRequests = (documentId: string): SplitDocumentRequest[] => {
        return this.documentStore.splitRequests.get(documentId) ?? [];
    }

    addSplitRequest = (documentId: string, splitRequest: SplitDocumentRequest): void => {
        const selectedRequest = this.documentStore.splitRequests.get(documentId);
        if (selectedRequest)
            this.documentStore.splitRequests.get(documentId)?.push(splitRequest);
        else
            this.documentStore.splitRequests.set(documentId, [splitRequest]);
    }

    clearSplitRequests = (documentId: string): void => {
        this.documentStore.splitRequests.delete(documentId);
    }

    downloadDocument = async (url: string, filename: string): Promise<void> => {
        const response = await fetch(url);
        if (!response.ok) {
            toast.error('Failed to download document.');
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const safeFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = safeFilename;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        toast.success('Document downloaded successfully!');
    }
}
